import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PlusIcon } from "lucide-react";
import PersonaAvatar from "./PersonaAvatar";
import usePersonaStore from "../../Store/PersonaStore";
import useAuthStore from "../../Store/AuthStore";
import CreatePersona from "./CreatePersona";
import "../Styles/tooltip.scss";
import Tooltip from "../ui/tooltip.jsx";
import { ScaleOnHover, AnimatedDiv } from "../ui/Motion";
import { useTranslation } from "react-i18next";

export default function PersonaSelector({ hideCreate = false, hideSuggested = false }) {
  const { t } = useTranslation();
  const {
    userPersonas,
    selectedPersona,
    setSelectedPersona,
    personaName,
    openPersonaForm,
    showGeneratedPersonaAvatar,
    showFullPersona,
    isPersonaShrinking,
    handleAvatarClick,
    showCreatePersonaForUnauthUser,
  } = usePersonaStore();

  const { isAuthenticated } = useAuthStore();

  const avatarRef = useRef(null);
  const selectedPersonaRef = useRef(null);
  const personasContainerRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({ left: 0, bottom: 0 });
  const [personasWidth, setPersonasWidth] = useState('auto');

  // Import isSearching from SearchStore
  const isSearching = require("../../Store/SearchStore.jsx").default((state) => state.isSearching);

  // Calculate dynamic width for personas container
  useEffect(() => {
    if (personasContainerRef.current) {
      const container = personasContainerRef.current;
      const scrollWidth = container.scrollWidth;
      const parentWidth = container.parentElement?.offsetWidth || 0;

      const isSuggestedPersonaShowing = !hideSuggested && (showGeneratedPersonaAvatar || showCreatePersonaForUnauthUser) && personaName;
      const reservedSpace = isSuggestedPersonaShowing ? 100 : 0;

      if (scrollWidth > parentWidth - reservedSpace) {
        setPersonasWidth(`${parentWidth - reservedSpace}px`);
      } else {
        setPersonasWidth('auto');
      }
    }
  }, [userPersonas.length, showGeneratedPersonaAvatar, showCreatePersonaForUnauthUser, personaName, hideSuggested]);

  // Calculate position to keep CreatePersona component on screen
  useEffect(() => {
    const updatePosition = () => {
      const isSuggestedPersonaShowing = !hideSuggested && (showGeneratedPersonaAvatar || showCreatePersonaForUnauthUser) && personaName;
      if (isSuggestedPersonaShowing && avatarRef.current) {
        const avatarRect = avatarRef.current.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const padding = 16;
        const componentMaxWidth = Math.min(425, screenWidth - (padding * 2));

        let leftPos;
        if (screenWidth < 640) {
          leftPos = (screenWidth - componentMaxWidth) / 2;
        } else {
          leftPos = avatarRect.left;
          const rightEdge = leftPos + componentMaxWidth;

          if (rightEdge > screenWidth - padding) {
            leftPos = Math.max(padding, avatarRect.right - componentMaxWidth);
          }

          leftPos = Math.max(padding, leftPos);
        }

        const bottomPos = screenHeight - avatarRect.top + 8;

        setPopupPosition({ left: leftPos, bottom: bottomPos });
      }
    };

    updatePosition();

    let intervalId;
    const isSuggestedPersonaShowing = !hideSuggested && (showGeneratedPersonaAvatar || showCreatePersonaForUnauthUser) && personaName;
    if (isSuggestedPersonaShowing) {
      intervalId = setInterval(updatePosition, 16);

      setTimeout(() => {
        if (intervalId) clearInterval(intervalId);
      }, 350);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showGeneratedPersonaAvatar, showCreatePersonaForUnauthUser, showFullPersona, personaName, hideSuggested]);

  // Scroll selected persona into view
  useEffect(() => {
    if (selectedPersona && selectedPersonaRef.current) {
      selectedPersonaRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [selectedPersona]);

  const handlePersonaSelect = (persona) => {
    if (selectedPersona && selectedPersona._id === persona._id) {
      setSelectedPersona(null);
    } else {
      setSelectedPersona(persona);
    }
  };

  const handleCreateClick = () => {
    openPersonaForm(null, true);
  };

  return (
    <>
      <div className="flex items-center w-full overflow-hidden">
        {isAuthenticated && !hideCreate && (
          <Tooltip
            content={t("persona.createPersonaTooltip")}
            placement="top"
            animation="shift-away"
            theme="green-tooltip"
          >
            <ScaleOnHover
              className="flex justify-center cursor-pointer flex-shrink-0 p-[1px]"
              onClick={handleCreateClick}
            >
              <div className="w-11 h-11 rounded-full bg-red-400 flex items-center justify-center hover:bg-red-500 transition-colors">
                <PlusIcon className="text-white w-6 h-6" />
              </div>
            </ScaleOnHover>
          </Tooltip>
        )}

        <div
          ref={personasContainerRef}
          className={`flex items-center gap-3 overflow-x-auto py-2 scrollbar-hide ${!hideCreate && isAuthenticated ? 'ml-3' : ''}`}
          style={{
            width: personasWidth,
            maxWidth: '85vw',
            flexShrink: 1,
            minWidth: 0
          }}
        >
          {userPersonas.map((persona) => (
            <div
              key={persona._id}
              ref={selectedPersona && selectedPersona._id === persona._id ? selectedPersonaRef : null}
              className="flex-shrink-0"
            >
              <PersonaAvatar
                persona={persona}
                isSelected={
                  selectedPersona && selectedPersona._id === persona._id
                }
                onClick={() => handlePersonaSelect(persona)}
              />
            </div>
          ))}
        </div>

        {!hideSuggested && (showGeneratedPersonaAvatar || showCreatePersonaForUnauthUser) && personaName && (
          <div className={`flex items-center flex-shrink-0 ${isAuthenticated || userPersonas.length > 0 ? 'ml-3' : ''}`}>
            <ScaleOnHover
              ref={avatarRef}
              className="cursor-pointer relative z-10"
              onClick={handleAvatarClick}
              as={motion.div}
              animate={{
                y: (isAuthenticated && !showFullPersona) ? 29 : 0,
              }}
              transition={{ duration: 0.3 }}
            >
              <div
                className={`w-11 h-11 rounded-full border-2 border-red-500 flex items-center justify-center bg-neutral-100 transition-opacity ${!showFullPersona ? 'opacity-30' : 'opacity-100'
                  }`}
              >
                <span className={`text-xl font-bold text-red-500 transition-opacity ${!showFullPersona ? 'opacity-30' : 'opacity-100'
                  }`}>
                  {isSearching ? '?' : personaName.charAt(0).toUpperCase()}
                </span>
              </div>
            </ScaleOnHover>
          </div>
        )}
      </div>

      {!hideSuggested && (showGeneratedPersonaAvatar || showCreatePersonaForUnauthUser) && personaName && (
        <AnimatedDiv
          className="fixed z-[100] w-full max-w-[425px] px-4"
          style={{
            left: `${popupPosition.left}px`,
            bottom: `${popupPosition.bottom}px`,
            maxWidth: 'calc(100vw - 2rem)',
            pointerEvents: showFullPersona && !isPersonaShrinking ? 'auto' : 'none',
          }}
          as={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: showFullPersona && !isPersonaShrinking ? 1 : 0,
            y: showFullPersona && !isPersonaShrinking ? 0 : 10,
            scale: showFullPersona && !isPersonaShrinking ? 1 : 0.8,
          }}
          transition={{ duration: 0.3 }}
        >
          <CreatePersona />
        </AnimatedDiv>
      )}
    </>
  );
}